import { useEffect, useRef } from "react";
import Link from "next/link";
import { ArrowRight, CalendarDays } from "lucide-react";
import { useFestivalExperience, useFestivalLocLang } from "./FestivalExperienceContext";
import { festivalThemeStyle } from "@/commons/festivalExperience/designPresets";
import { DiyaIcon } from "./FestivalOrnaments";
import ObservanceDayModule from "./ObservanceDayModule";
import FestivalPoojaCard from "./FestivalPoojaCard";
import MandalaWatermark from "@/components/v2/landing/MandalaWatermark";
import { smoothScrollTo } from "@/lib/scroll";
import { FaWhatsapp } from "react-icons/fa";
import { LEAD_CTAS, LeadTag, WhatsAppButton, CallLink, openFestivalCallback, festivalWaLink, trackLeadClick } from "./leadCtas";

const SLOT = "home.hero";
// Literal class names so Tailwind can see them; grid width follows the mapping size.
const MD_COLS = { 1: "md:grid-cols-2", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" };

export const todayLineOf = (experience) => {
  const { activeDay, phase, firstDay } = experience;
  if (activeDay) return activeDay.colorName ? `${activeDay.name} · colour of the day: ${activeDay.colorName}` : activeDay.name;
  if (phase === "teaser" && firstDay) return `Begins ${firstDay.shortDate} · ${firstDay.name}`;
  if (phase === "afterglow") return "All days complete";
  return experience.primaryCampaign.name;
};

export const poojaHeadingOf = (experience) =>
  experience.mappingSource === "child" && experience.activeDay
    ? `For ${experience.activeDay.name.split(" · ")[0]}`
    : `${experience.primaryCampaign.festivalName} pujas`;

const AUTO_SCROLL_DELAY_MS = 700;
const AUTO_SCROLL_KEY = "fx-home-autoscroll";

// While a campaign is live, a fresh homepage landing glides down to this
// section once per browser session. Skipped when the URL has a hash, the
// browser restored a scroll position, or the visitor touches the page first.
const useLiveCampaignAutoScroll = (ref, experience) => {
  const isLive = experience?.phase === "live";
  const campaignKey = experience?.primaryCampaign?.landingHref;

  useEffect(() => {
    if (!isLive || !campaignKey || window.location.hash || window.scrollY > 40) return;
    const storageKey = `${AUTO_SCROLL_KEY}:${campaignKey}`;
    try {
      if (sessionStorage.getItem(storageKey)) return;
    } catch {}

    let cancelled = false;
    const cancel = () => { cancelled = true; };
    const events = ["wheel", "touchstart", "keydown", "mousedown"];
    events.forEach((e) => window.addEventListener(e, cancel, { once: true, passive: true }));

    const timer = window.setTimeout(() => {
      events.forEach((e) => window.removeEventListener(e, cancel));
      const el = ref.current;
      if (cancelled || !el || window.scrollY > 40) return;
      try { sessionStorage.setItem(storageKey, "1"); } catch {}
      // Land just below the fixed nav (which also holds the festival strip).
      const navBottom = document.querySelector("nav")?.getBoundingClientRect().bottom || 72;
      const top = el.getBoundingClientRect().top + window.scrollY - navBottom;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) window.scrollTo(0, top);
      else smoothScrollTo(top, 900);
    }, AUTO_SCROLL_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, cancel));
    };
  }, [ref, isLive, campaignKey]);
};

// Layer 2 on the homepage (PRD §4). Sits below the standard hero so search,
// language cards and InstaBook stay where returning visitors expect them.
const FestivalHero = () => {
  const { experience } = useFestivalExperience();
  const { language, location } = useFestivalLocLang();
  const sectionRef = useRef(null);
  useLiveCampaignAutoScroll(sectionRef, experience);
  if (!experience?.primaryCampaign) return null;

  const { copy, primaryCampaign, activeDay, poojas = [], nextObservance, days = [] } = experience;
  const poojaRow = poojas.slice(0, 4);

  return (
    <section
      ref={sectionRef}
      id="festival"
      style={festivalThemeStyle(primaryCampaign.themePreset, activeDay?.color)}
      data-fx-impression={SLOT}
      className="relative overflow-hidden bg-[hsl(var(--fx-bg))] text-[hsl(var(--fx-fg))]"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_85%_10%,hsl(var(--fx-bg-2)/0.85),transparent_70%),radial-gradient(ellipse_50%_50%_at_10%_100%,hsl(var(--fx-accent)/0.18),transparent_70%)]" />
      <MandalaWatermark className="pointer-events-none absolute -right-40 -top-40 h-[640px] w-[640px] text-[hsl(var(--fx-accent))] opacity-[0.10]" />

      <div className="container relative py-12 md:py-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.05fr_1fr]">
          <div className="min-w-0 space-y-5">
            {copy.eyebrow && (
              <span className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--fx-accent)/0.45)] bg-[hsl(var(--fx-accent)/0.12)] px-3.5 py-1.5 text-xs md:text-sm font-semibold font-sans text-[hsl(var(--fx-accent))]">
                <DiyaIcon className="h-4 w-4" /> {copy.eyebrow}
              </span>
            )}
            {copy.headline && <h2 className="font-playfair text-3xl md:text-5xl leading-tight !text-[hsl(var(--fx-fg))]">{copy.headline}</h2>}
            {copy.sub && <p className="max-w-xl font-sans text-base md:text-lg leading-relaxed text-[hsl(var(--fx-muted))]">{copy.sub}</p>}
            <div className="flex flex-col gap-3 pt-1 sm:flex-row">
              <Link
                href={primaryCampaign.landingHref}
                data-fx-slot={SLOT}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-[hsl(var(--fx-accent))] px-7 font-sans text-base font-bold text-[hsl(var(--fx-accent-fg))] shadow-[0_8px_30px_-6px_hsl(var(--fx-accent)/0.6)] transition-transform hover:-translate-y-0.5"
              >
                {copy.cta || `Explore ${primaryCampaign.festivalName}`} <ArrowRight className="h-4 w-4" />
              </Link>
              {LEAD_CTAS.heroWhatsapp ? (
                <div className="relative">
                  <LeadTag n={2} />
                  <WhatsAppButton slot={SLOT} label="Ask a pandit on WhatsApp" className="min-h-[48px] w-full px-6 text-base" />
                </div>
              ) : (
                <Link
                  href={`/${language}/${location}/pooja`}
                  className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-[hsl(var(--fx-fg)/0.35)] px-7 font-sans text-base font-semibold text-[hsl(var(--fx-fg))] hover:bg-white/5"
                >
                  Browse all pujas
                </Link>
              )}
            </div>
            {LEAD_CTAS.heroWhatsapp && (
              <p className="font-sans text-sm text-[hsl(var(--fx-muted))]">
                or{" "}
                <CallLink slot={SLOT} className="font-semibold text-[hsl(var(--fx-fg))] underline decoration-[hsl(var(--fx-accent))] underline-offset-4">
                  call 9869 92 92 92
                </CallLink>{" "}
                · <Link href={`/${language}/${location}/pooja`} className="hover:underline">browse all pujas</Link>
              </p>
            )}
          </div>

          <div className="min-w-0 rounded-3xl border border-[hsl(var(--fx-border)/0.3)] bg-[hsl(var(--fx-card)/0.55)] p-5 md:p-6 backdrop-blur-md">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-sans text-[11px] font-bold uppercase tracking-[0.18em] text-[hsl(var(--fx-muted))]">{activeDay ? "Today" : primaryCampaign.name}</p>
                <p className="font-playfair text-lg md:text-xl font-bold">{todayLineOf(experience)}</p>
              </div>
              {activeDay?.color && (
                <span className="h-10 w-10 shrink-0 rounded-full border-2 border-[hsl(var(--fx-accent))]" style={{ backgroundColor: activeDay.color }} aria-hidden="true" />
              )}
            </div>
            {days.length > 0 && <ObservanceDayModule experience={experience} />}
            {/* Same-day bookings aren't served, so this always points at the next day. */}
            {LEAD_CTAS.todayWhatsapp && nextObservance && (
              <a
                href={festivalWaLink({
                  campaign: primaryCampaign,
                  slot: "home.next",
                  intent: `I'd like to book a pandit in advance for ${nextObservance.name} (${nextObservance.shortDate})`,
                  city: location,
                })}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackLeadClick(primaryCampaign, "whatsapp", "home.next")}
                className="relative mt-4 flex items-center gap-3 rounded-xl border border-[#1fa855]/50 bg-[#1fa855]/15 px-3.5 py-2.5 font-sans text-sm transition-colors hover:bg-[#1fa855]/25"
              >
                <LeadTag n={12} className="-right-2 -top-3" />
                <FaWhatsapp className="h-5 w-5 shrink-0 text-[#5fd68c]" />
                <span className="min-w-0 flex-1">
                  <span className="font-semibold text-[hsl(var(--fx-fg))]">
                    Book ahead for {nextObservance.name.split(" · ").pop()} · {nextObservance.shortDate}
                  </span>
                  <span className="block text-xs text-[hsl(var(--fx-muted))]">Pandit slots fill up fast — reserve yours on WhatsApp</span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-[#5fd68c]" />
              </a>
            )}
            {nextObservance && (
              <p className="mt-4 flex items-center gap-2 border-t border-white/10 pt-3 font-sans text-xs text-[hsl(var(--fx-muted))]">
                <CalendarDays className="h-4 w-4 shrink-0 text-[hsl(var(--fx-accent))]" />
                Next: <span className="font-semibold text-[hsl(var(--fx-fg))]">{nextObservance.name}</span> · {nextObservance.shortDate}
              </p>
            )}
          </div>
        </div>

        {poojaRow.length > 0 && (
          <div className="mt-10">
            <h3 className="mb-4 font-playfair text-xl md:text-2xl font-bold !text-[hsl(var(--fx-fg))]">{poojaHeadingOf(experience)}</h3>
            <div className={`-mx-4 flex snap-x gap-4 overflow-x-auto scrollbar-hide px-4 pb-2 md:mx-0 md:grid ${MD_COLS[poojaRow.length] || "md:grid-cols-4"} md:px-0`}>
              {poojaRow.map((p, i) => (
                <div key={`${p.slug}-${p.linkLocation || ""}-${p.linkLanguage || ""}`} className="w-[70vw] max-w-[280px] shrink-0 snap-start md:w-auto md:max-w-none">
                  <FestivalPoojaCard pooja={p} slotKey={SLOT} rank={i + 1} />
                </div>
              ))}
            </div>
            {LEAD_CTAS.heroWhatsapp && (
              <button
                type="button"
                onClick={() => openFestivalCallback({ slot: "home.hero.consult" })}
                className="relative mt-5 flex w-full flex-col items-start gap-1 rounded-2xl border border-dashed border-[hsl(var(--fx-accent)/0.6)] bg-[hsl(var(--fx-accent)/0.08)] px-5 py-4 text-left transition-colors hover:bg-[hsl(var(--fx-accent)/0.14)] sm:flex-row sm:items-center sm:justify-between"
              >
                <LeadTag n={2} />
                <span className="font-sans text-sm md:text-base">
                  <span className="font-bold text-[hsl(var(--fx-fg))]">Not sure which puja is right for your family?</span>{" "}
                  <span className="text-[hsl(var(--fx-muted))]">A pandit will guide you — free, no obligation.</span>
                </span>
                <span className="inline-flex shrink-0 items-center gap-1 font-sans text-sm font-bold text-[hsl(var(--fx-accent))]">
                  Get a free consultation <ArrowRight className="h-4 w-4" />
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default FestivalHero;
